import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AdminService, KnowledgePage } from '../../../service/admin.service';
import { KnowledgeDialogComponent, KnowledgeDialogData } from './dialogs/knowledge-dialog/knowledge-dialog.component';

@Component({
  selector: 'app-knowledge-base',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatCardModule,
    MatChipsModule,
    MatToolbarModule,
    MatTooltipModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './knowledge-base.component.html',
  styleUrl: './knowledge-base.component.less'
})
export class KnowledgeBaseComponent implements OnInit {
  adminService = inject(AdminService);
  dialog = inject(MatDialog);
  snackBar = inject(MatSnackBar);

  pages: KnowledgePage[] = [];
  dataSource = new MatTableDataSource<KnowledgePage>([]);
  displayedColumns: string[] = ['name', 'description', 'published', 'updated_at', 'actions'];
  isLoading = false;

  get publishedCount(): number {
    return this.pages.filter(p => p.published).length;
  }

  ngOnInit() {
    this.loadPages();
  }

  loadPages() {
    this.isLoading = true;
    this.adminService.getKnowledgePages().subscribe({
      next: (pages) => {
        this.pages = pages;
        this.dataSource.data = pages;
        this.isLoading = false;
      },
      error: (error) => {
        this.snackBar.open('Failed to load knowledge base pages', 'Close', { duration: 3000 });
        this.isLoading = false;
        console.error('Error loading knowledge pages:', error);
      }
    });
  }

  startCreate() {
    const dialogData: KnowledgeDialogData = {
      isEdit: false,
      existingNames: this.pages.map(p => p.name)
    };

    const dialogRef = this.dialog.open(KnowledgeDialogComponent, {
      data: dialogData,
      width: '800px',
      maxWidth: '95vw'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.createPage(result);
      }
    });
  }

  startEdit(page: KnowledgePage) {
    const dialogData: KnowledgeDialogData = {
      isEdit: true,
      page,
      existingNames: this.pages.filter(p => p.id !== page.id).map(p => p.name)
    };

    const dialogRef = this.dialog.open(KnowledgeDialogComponent, {
      data: dialogData,
      width: '800px',
      maxWidth: '95vw'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.updatePage(result);
      }
    });
  }

  startDelete(page: KnowledgePage) {
    const confirmed = confirm(`Are you sure you want to delete "${page.name}"?\n\nIf Cult Bot's AI is still referencing this page name, it will start getting an "Unknown page" error.`);

    if (confirmed) {
      this.deletePage(page.id);
    }
  }

  private createPage(page: KnowledgePage) {
    this.adminService.createKnowledgePage(page).subscribe({
      next: () => {
        this.snackBar.open('Knowledge page created successfully', 'Close', { duration: 3000 });
        this.loadPages();
      },
      error: (error) => {
        this.snackBar.open(error?.error?.message || 'Failed to create knowledge page', 'Close', { duration: 3000 });
        console.error('Error creating knowledge page:', error);
      }
    });
  }

  private updatePage(page: KnowledgePage) {
    this.adminService.updateKnowledgePage(page).subscribe({
      next: () => {
        this.snackBar.open('Knowledge page updated successfully', 'Close', { duration: 3000 });
        this.loadPages();
      },
      error: (error) => {
        this.snackBar.open(error?.error?.message || 'Failed to update knowledge page', 'Close', { duration: 3000 });
        console.error('Error updating knowledge page:', error);
      }
    });
  }

  private deletePage(id: number) {
    this.adminService.deleteKnowledgePage(id).subscribe({
      next: () => {
        this.snackBar.open('Knowledge page deleted successfully', 'Close', { duration: 3000 });
        this.loadPages();
      },
      error: (error) => {
        this.snackBar.open('Failed to delete knowledge page', 'Close', { duration: 3000 });
        console.error('Error deleting knowledge page:', error);
      }
    });
  }

  getContentPreview(content: string): string {
    if (content.length <= 100) {
      return content;
    }
    return content.substring(0, 100) + '...';
  }
}
